// /settings - partner_owner edits address, manages 2FA, T4A consent.
// Phase 6.1 Chunk h.

import { useEffect, useState } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { Card, CardLabel, Button, Input, Select, colors, headingStyle, pageWrapperStyle, mutedStyle, errorBannerStyle, linkStyle, useToast } from '../components/ui';
import { useLang, fmt } from '../i18n';

interface Settings {
  name: string;
  slug: string;
  legal_entity_masked: string | null;
  legal_address: {
    line1?: string;
    line2?: string;
    city?: string;
    postal_code?: string;
    province?: string;
    country?: string;
  } | null;
  qualification_tier: string;
  cert_status: string;
  cert_expires_at: string | null;
  dpa_signed_at: string | null;
  msa_signed_at: string | null;
}

const PROVINCE_OPTIONS = [
  { value: 'QC', label: 'QC' },
  { value: 'ON', label: 'ON' },
  { value: 'AB', label: 'AB' },
  { value: 'BC', label: 'BC' },
  { value: 'MB', label: 'MB' },
  { value: 'SK', label: 'SK' },
  { value: 'NS', label: 'NS' },
  { value: 'NB', label: 'NB' },
  { value: 'NL', label: 'NL' },
  { value: 'PE', label: 'PE' },
];

export default function SettingsPage() {
  const { t } = useLang();
  const toast = useToast();
  const [data, setData] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Address form state
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [postal, setPostal] = useState('');
  const [province, setProvince] = useState('QC');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/partner/settings');
        if (!res.ok) {
          if (!cancelled) {
            setError(`HTTP ${res.status}`);
            setLoading(false);
          }
          return;
        }
        const json = (await res.json()) as Settings;
        if (!cancelled) {
          setData(json);
          if (json.legal_address) {
            setLine1(json.legal_address.line1 ?? '');
            setLine2(json.legal_address.line2 ?? '');
            setCity(json.legal_address.city ?? '');
            setPostal(json.legal_address.postal_code ?? '');
            setProvince(json.legal_address.province ?? 'QC');
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('network_error');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function saveAddress() {
    setSaving(true);
    try {
      const res = await fetch('/api/partner/settings', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          address: {
            line1,
            line2: line2 || undefined,
            city,
            postal_code: postal,
            province,
            country: 'CA',
          },
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.show('success', t.common.saved);
      } else {
        toast.show('error', `${t.common.error}: ${(json as { error?: string }).error ?? 'unknown'}`);
      }
    } catch {
      toast.show('error', t.common.networkError);
    } finally {
      setSaving(false);
    }
  }

  return (
    <PortalLayout>
      <div style={pageWrapperStyle}>
        <h1 style={headingStyle}>
          {t.settings.title}
        </h1>

        {loading && <div style={mutedStyle}>{t.common.loading}</div>}

        {error && (
          <div style={errorBannerStyle}>
            {fmt(t.settings.errorMsg, { error })}
          </div>
        )}

        {data && (
          <>
            {/* Org identity */}
            <Card>
              <CardLabel>{t.settings.orgSection}</CardLabel>
              <div style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '4px' }}>
                {data.name}
              </div>
              <div style={{ ...mutedStyle, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                {data.slug}
              </div>

              <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                <div>
                  <CardLabel style={{ marginBottom: '4px' }}>{t.settings.orgTier}</CardLabel>
                  <div>{data.qualification_tier}</div>
                </div>
                <div>
                  <CardLabel style={{ marginBottom: '4px' }}>{t.settings.orgCert}</CardLabel>
                  <div>{data.cert_status}</div>
                </div>
                <div>
                  <CardLabel style={{ marginBottom: '4px' }}>{t.settings.orgCertExpires}</CardLabel>
                  <div>{data.cert_expires_at ? new Date(data.cert_expires_at).toLocaleDateString() : t.common.notSet}</div>
                </div>
              </div>
            </Card>

            {/* Legal entity (read-only) */}
            <Card>
              <CardLabel>{t.settings.legalEntity}</CardLabel>
              <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', marginBottom: '8px' }}>
                {data.legal_entity_masked ?? t.common.notOnFile}
              </div>
              <p style={{ ...mutedStyle, fontSize: '0.8rem' }}>
                {t.settings.legalEntityReadonly}
              </p>
            </Card>

            {/* Mailing address */}
            <Card>
              <CardLabel>{t.settings.addressSection}</CardLabel>
              <p style={{ ...mutedStyle, fontSize: '0.8rem', marginBottom: '12px' }}>
                {t.settings.addressDesc}
              </p>
              <Input
                label={t.settings.fieldStreet}
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
              />
              <Input
                label={t.settings.fieldApt}
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
                <div style={{ flex: '1 1 auto', minWidth: '120px' }}>
                  <Input
                    label={t.settings.fieldCity}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div style={{ flex: '1 1 auto', minWidth: '120px' }}>
                  <Select
                    label={t.settings.fieldProvince}
                    options={PROVINCE_OPTIONS}
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                  />
                </div>
                <div style={{ flex: '1 1 auto', minWidth: '120px' }}>
                  <Input
                    label={t.settings.fieldPostal}
                    placeholder={t.settings.postalPlaceholder}
                    value={postal}
                    onChange={(e) => setPostal(e.target.value.toUpperCase())}
                  />
                </div>
              </div>
              <Button variant="primary" busy={saving} onClick={saveAddress}>
                {t.settings.saveAddress}
              </Button>
            </Card>

            {/* 2FA */}
            <Card>
              <CardLabel>{t.settings.twoFaSection}</CardLabel>
              <p style={{ ...mutedStyle, marginBottom: '12px' }}>
                {t.settings.twoFaDesc}
              </p>
              <a
                href="/api/auth/start?next=%2Fsettings"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.cyanBorder}`,
                  ...linkStyle,
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                }}
              >
                {t.settings.twoFaLink}
              </a>
            </Card>

            {/* Compliance status */}
            <Card>
              <CardLabel>{t.settings.complianceSection}</CardLabel>
              <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', lineHeight: 2 }}>
                <li>
                  {t.settings.dpaSigned}{' '}
                  {data.dpa_signed_at ? (
                    <span style={{ color: colors.success }}>
                      {'✓'} {new Date(data.dpa_signed_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span style={{ color: colors.danger }}>{t.settings.notSigned}</span>
                  )}
                </li>
                <li>
                  {t.settings.msaSigned}{' '}
                  {data.msa_signed_at ? (
                    <span style={{ color: colors.success }}>
                      {'✓'} {new Date(data.msa_signed_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span style={{ color: colors.danger }}>{t.settings.notSigned}</span>
                  )}
                </li>
              </ul>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
